import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetUrlParams } from '../components/shared/useUrlPagination';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useMedia } from '../contexts/MediaContext';
import { Card, CardContent } from '../components/ui/card';
import { MobileSectionMenu } from '../components/shared/MobileSectionMenu';
import { Button } from '../components/ui/button';
import { Video, Play, Clapperboard, Shield, Share2, Loader2 } from 'lucide-react';
import { EventVideos } from '../components/media/EventVideos';
import { LiveVideos } from '../components/media/LiveVideos';
import { RemoteDownloads } from '../components/media/RemoteDownloads';
import { SharedEvidence } from '../components/media/SharedEvidence';

export const Media = () => {
  const location = useLocation();
  const { isSubpageEnabled, loading } = useFeatureFlags();
  const { persistedActiveTab, setPersistedActiveTab } = useMedia();
  const setUrlParams = useSetUrlParams();
  // Deep-link the tab; reset table page params when switching tabs (sizes are
  // kept — they're the user's remembered rows-per-page preference).
  const changeTab = (tab) => {
    setActiveTab(tab);
    setUrlParams({ tab, page: null, dl_page: null });
  };

  const hasEventVideos = isSubpageEnabled('media_event_videos');
  const hasLiveVideos = isSubpageEnabled('media_live_videos');
  const hasRemoteMedia = isSubpageEnabled('media_remote_downloads');
  const hasSharedEvidence = isSubpageEnabled('media_shared_evidence');

  const allowedTabsMap = () => ({
    events: hasEventVideos,
    streams: hasLiveVideos,
    downloads: hasRemoteMedia,
    shared: hasSharedEvidence,
  });

  const getDefaultTab = () => {
    if (hasEventVideos) return 'events';
    if (hasLiveVideos) return 'streams';
    if (hasRemoteMedia) return 'downloads';
    if (hasSharedEvidence) return 'shared';
    return null;
  };

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const getRequestedTab = () => {
    const allowed = allowedTabsMap();
    return allowed[requestedTab] ? requestedTab : null;
  };

  const [activeTab, setActiveTab] = useState(() => {
    const requested = getRequestedTab();
    if (requested) return requested;
    const allowed = allowedTabsMap();
    if (persistedActiveTab && allowed[persistedActiveTab]) return persistedActiveTab;
    return getDefaultTab();
  });

  // Mirror activeTab back into persistence (URL still wins on initial load)
  useEffect(() => {
    if (activeTab && activeTab !== persistedActiveTab) setPersistedActiveTab(activeTab);
  }, [activeTab, persistedActiveTab, setPersistedActiveTab]);

  useEffect(() => {
    const requested = getRequestedTab();
    if (requested && activeTab !== requested) {
      setActiveTab(requested);
      return;
    }
    const allowed = allowedTabsMap();
    if (!activeTab || allowed[activeTab] !== true) {
      const defaultTab = getDefaultTab();
      if (defaultTab) setActiveTab(defaultTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, activeTab, hasEventVideos, hasLiveVideos, hasRemoteMedia, hasSharedEvidence]);

  return (
    <div className="space-y-6" data-testid="media-page">
      {/* Page Header */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <Video className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Media Management</h1>
          <p className="text-sm text-muted-foreground">Manage event recordings, live streams, and remote media playback</p>
        </div>
      </div>

      {/* Sub-navigation Tabs */}
      {loading ? (
        <Card data-testid="media-loading">
          <CardContent className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading media permissions...
          </CardContent>
        </Card>
      ) : <>
      <MobileSectionMenu
        items={[
          hasEventVideos && { id: 'events', label: 'Event Videos', icon: Video },
          hasLiveVideos && { id: 'streams', label: 'Live Videos', icon: Play },
          hasRemoteMedia && { id: 'downloads', label: 'Remote Media', icon: Clapperboard },
          hasSharedEvidence && { id: 'shared', label: 'Shared Evidence', icon: Share2 },
        ].filter(Boolean)}
        activeId={activeTab}
        onChange={changeTab}
        testIdPrefix="media-tabs"
      />
      <div className="hidden sm:flex gap-2 flex-wrap">
        {hasEventVideos && (
          <Button
            onClick={() => changeTab('events')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'events'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-events"
          >
            <Video className="w-4 h-4" />
            Event Videos
          </Button>
        )}
        {hasLiveVideos && (
          <Button
            onClick={() => changeTab('streams')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'streams'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-streams"
          >
            <Play className="w-4 h-4" />
            Live Videos
          </Button>
        )}
        {hasRemoteMedia && (
          <Button
            onClick={() => changeTab('downloads')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'downloads'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-downloads"
          >
            <Clapperboard className="w-4 h-4" />
            Remote Media
          </Button>
        )}
        {hasSharedEvidence && (
          <Button
            onClick={() => changeTab('shared')}
            className={`gap-2 min-w-[180px] justify-center ${activeTab === 'shared'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-shared"
          >
            <Share2 className="w-4 h-4" />
            Shared Evidence
          </Button>
        )}
      </div></>}

      {/* No Access */}
      {!loading && !activeTab && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">No Access</p>
            <p className="text-sm text-muted-foreground mt-2">You don't have permission to access any sections of Media Management.</p>
            <p className="text-xs text-muted-foreground mt-4">Contact your administrator to request access.</p>
          </CardContent>
        </Card>
      )}

      {/* Tab Content */}
      {!loading && activeTab === 'events' && hasEventVideos && <EventVideos />}
      {!loading && activeTab === 'streams' && hasLiveVideos && <LiveVideos />}
      {!loading && activeTab === 'downloads' && hasRemoteMedia && <RemoteDownloads />}
      {!loading && activeTab === 'shared' && hasSharedEvidence && <SharedEvidence />}
    </div>
  );
};
